/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import { useApi } from '@/hooks'

import { comparePath, missingFilter, runMigration } from './runMigration'
import { RunSummary } from './RunSummary'
import { SideBySide } from './SideBySide'
import type { CompareSpec, MigrationFilters, RunResult, RunTarget } from './types'

interface Props {
  itemId: string
  target: RunTarget
  filters: MigrationFilters
  search: string
  needsAsset: boolean
  compare?: CompareSpec | null
  label: string
  /** Called after each run, e.g. to refresh the run history. */
  onRun?: (result?: RunResult) => void
}

/** A Run button, then the verdict and the first ten rows of each side. */
export const RunPanel: React.FC<Props> = ({ itemId, target, filters, search, needsAsset, compare, label, onRun }) => {
  const { api } = useApi()
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<RunResult>()
  const [error, setError] = useState('')
  const missing = missingFilter(filters, { asset: needsAsset })

  const run = async () => {
    setRunning(true)
    setError('')
    const out = await runMigration(api, itemId, target, filters)
    setRunning(false)
    setResult(out.result)
    setError(out.error ?? '')
    onRun?.(out.result)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={running || Boolean(missing)}
          onClick={() => void run()}
        >
          {running ? <span className="loading loading-spinner loading-xs" /> : null}
          {label}
        </button>
        {missing && <span className="text-sm text-base-content/60">{missing}</span>}
        {error && <span className="text-sm text-error">{error}</span>}
      </div>
      {result && (
        <>
          <RunSummary result={result} compareTo={comparePath(itemId, target, search)} />
          {result.compare && <SideBySide rows={result.compare.rows} keyFields={compare?.key} limit={10} />}
        </>
      )}
    </div>
  )
}
